import type { ApiError } from "@shared";

/**
 * Erreur d'API portant le CODE renvoye par le serveur.
 * Le client choisit sa cle i18n a partir de ce code : les messages montres a
 * le jeune sont ecrits en francais dans les fichiers de langue, jamais
 * fabriques par le serveur.
 */
export class ApiFetchError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiFetchError";
    this.status = status;
    this.code = code;
  }
}

/** Code utilise quand le reseau est coupe ou le serveur injoignable. */
export const NETWORK_ERROR_CODE = "network_error";

/**
 * Appel JSON de l'API. `credentials: "same-origin"` transporte le cookie de
 * session, qui est httpOnly et donc invisible au JavaScript.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  // Avec FormData, le navigateur compose lui-meme le Content-Type pour y
  // inclure la frontiere du multipart : le fixer ici casserait l'envoi.
  const isMultipart = init?.body instanceof FormData;

  try {
    response = await fetch(`/api${path}`, {
      ...init,
      credentials: "same-origin",
      headers: isMultipart
        ? init?.headers
        : { "Content-Type": "application/json", ...init?.headers },
    });
  } catch {
    throw new ApiFetchError(0, NETWORK_ERROR_CODE, "Network unreachable");
  }

  if (!response.ok) {
    let code = "internal_error";
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as ApiError;
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // Reponse non-JSON (page d'erreur d'un proxy, par exemple).
    }
    throw new ApiFetchError(response.status, code, message);
  }

  // Une reponse SANS CORPS est une reussite, pas une anomalie : un 204 est la
  // facon normale de dire « c'est fait, il n'y a rien a renvoyer ». Sans ce
  // garde-fou, `json()` levait une erreur de syntaxe sur une requete qui avait
  // pourtant abouti, et l'appelant affichait un echec.
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/** Raccourci pour les requetes qui envoient un corps JSON. */
export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) });
}

/** Suppression : le serveur repond 204 sans corps. */
export async function apiDelete(path: string): Promise<void> {
  const response = await fetch(`/api${path}`, { method: "DELETE", credentials: "same-origin" });
  if (!response.ok) {
    let code = "internal_error";
    try {
      const body = (await response.json()) as ApiError;
      code = body.error?.code ?? code;
    } catch {
      // Reponse vide ou non-JSON.
    }
    throw new ApiFetchError(response.status, code, `HTTP ${response.status}`);
  }
}

/**
 * Envoi multipart (formulaire avec image).
 * On ne fixe PAS Content-Type : le navigateur doit le composer lui-meme pour y
 * inclure la frontiere ("boundary") du multipart.
 */
export function apiUpload<T>(path: string, method: "POST" | "PATCH", form: FormData): Promise<T> {
  return apiFetch<T>(path, { method, body: form });
}
